import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Loader } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uid } from "@/lib/uid";
import { toast } from "sonner";

const GREETING = "I'm your Sovereign Assistant — empowered by EIGOO Inc. I help with three things: your 60-second Ask video, your Aesthetic Vault portfolio, and the $99 Boarding Deposit. What do you need?";

const QUICK_PROMPTS = [
  "How do I shoot the 60-second Ask video?",
  "What images should I upload to the Aesthetic Vault?",
  "How does the $99 Boarding Deposit work?",
  "What is the prize for being chosen?",
];

const sessionKey = "siren_wingman_session";

export const Wingman = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => [{ id: uid(), role: "assistant", text: GREETING }]);
  // Chat session id (not a credential) — kept in sessionStorage so it dies with the tab.
  const sessionId = useRef(
    typeof window !== "undefined"
      ? (window.sessionStorage.getItem(sessionKey) ||
         (() => { const s = `siren-${uid()}`; window.sessionStorage.setItem(sessionKey, s); return s; })())
      : "siren-local"
  );
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: uid(), role: "user", text: trimmed }]);
    setBusy(true);
    try {
      const { data } = await api.post("/wingman/chat", {
        session_id: sessionId.current,
        message: trimmed,
      });
      setMessages((m) => [...m, { id: uid(), role: "assistant", text: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Wingman is offline. Try again shortly.");
      setMessages((m) => [...m, { id: uid(), role: "assistant", text: "Signal lost. Try once more." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <motion.button
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.4 }}
        className="fixed bottom-20 right-6 z-[80] group"
        data-testid="wingman-launcher"
        aria-label="Open Sovereign Assistant"
      >
        <div className="absolute inset-0 -m-1 pulse-gold rounded-full" />
        <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#C5A017] via-[#C9A227] to-[#F3E5AB] flex items-center justify-center shadow-2xl shadow-[#C9A227]/40 group-hover:scale-110 transition-transform">
          {open ? <X size={22} className="text-black"/> : <MessageCircle size={22} className="text-black" fill="currentColor"/>}
        </div>
        {!open && (
          <span className="hidden md:block absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap glass border border-[#C9A227]/40 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">
            Sovereign Assistant
          </span>
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-40 right-4 md:right-6 z-[90] w-[calc(100vw-2rem)] md:w-[400px] h-[68vh] md:h-[560px] flex flex-col glass border border-[#C9A227]/40 shadow-2xl shadow-black/60"
            style={{ borderRadius: 0 }}
            data-testid="wingman-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#C9A227]/20 bg-black/40">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C5A017] to-[#F3E5AB] flex items-center justify-center">
                <Sparkles size={16} className="text-black"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-black uppercase text-sm tracking-wider">Sovereign Assistant</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Empowered by EIGOO Inc</div>
              </div>
              <span className="w-2 h-2 rounded-full bg-green-400 pulse-gold" aria-label="online"/>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3 scroll-fade">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-[#C9A227] text-black font-medium"
                        : "border border-[#C9A227]/30 bg-black/40 text-white/90"
                    }`}
                    data-testid={`wingman-msg-${m.role}`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="border border-[#C9A227]/30 bg-black/40 px-3.5 py-2.5 text-[#C9A227] text-xs flex items-center gap-2">
                    <Loader size={12} className="animate-spin"/> Sovereign Assistant thinking…
                  </div>
                </div>
              )}
              <div ref={endRef}/>
            </div>

            {/* Quick prompts */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 grid grid-cols-1 gap-1.5">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q} onClick={() => send(q)} disabled={busy}
                    className="text-left text-[11px] text-white/70 hover:text-[#C9A227] border border-[#C9A227]/15 hover:border-[#C9A227]/50 px-3 py-1.5 transition-colors">
                    → {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="px-4 py-3 border-t border-[#C9A227]/20 bg-black/40 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Signal Wingman…"
                disabled={busy}
                className="flex-1 bg-transparent border-none outline-none text-white text-sm placeholder:text-white/30"
                data-testid="wingman-input"
              />
              <button type="submit" disabled={busy || !input.trim()}
                className="w-9 h-9 bg-[#C9A227] disabled:opacity-30 text-black flex items-center justify-center hover:brightness-110 transition-all"
                data-testid="wingman-send-btn">
                <Send size={14} fill="currentColor"/>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
